import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { type User, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { useAuth as useRsyncAuth } from '@honeyhimself/rsync-auth-react';
import type { UserInfo as RsyncUserInfo } from '@honeyhimself/rsync-auth';
import { auth } from '../config/firebase';
import { mockCurrentUser, mockInitialDbUser } from '@/constants/mockValues';
import * as userService from '@/services/user.service';
import { setRsyncTokenGetter, setActiveAuthProvider, setRsyncRefresher, setLogoutCallback } from '@/services/auth.service';
import type { DbUser, CreateUserDTO } from '@/types/user';

interface AuthContextType {
    currentUser: User | null;
    rsyncUser: RsyncUserInfo | null;
    activeProvider: 'firebase' | 'rsync' | null;
    dbUser: DbUser | null;
    loading: boolean;
    signInWithGoogle: () => Promise<void>;
    loginWithRsync: () => Promise<void>;
    createUser: (data: Omit<CreateUserDTO, 'authId' | 'email'>) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const isMock = import.meta.env.VITE_MOCK_AUTH === "true";

    const [currentUser, setCurrentUser] = useState<User | null>(isMock ? mockCurrentUser as any : null);
    const [dbUser, setDbUser] = useState<DbUser | null>(isMock ? mockInitialDbUser as any : null);
    const [loading, setLoading] = useState(true);

    // Rsync auth state — provided by the RsyncAuthProvider ancestor in App.tsx
    const { user: rsyncUser, isLoading: rsyncLoading, login: rsyncLogin, logout: rsyncLogout, getAccessToken, refresh: rsyncRefresh } = useRsyncAuth();

    // Track when each auth system has finished its initial check so we only
    // set loading=false once both have settled (prevents premature routing).
    const firebaseSettledRef = useRef(isMock); // mock: skip Firebase
    const rsyncSettledRef = useRef(isMock);     // mock: skip Rsync

    const activeProvider: 'firebase' | 'rsync' | null =
        currentUser ? 'firebase' : rsyncUser ? 'rsync' : null;

    // ── Register Rsync hooks with the axios interceptor ────────────────────────
    useEffect(() => {
        const isRsync = activeProvider === 'rsync';
        setRsyncTokenGetter(isRsync ? getAccessToken : null);
        setRsyncRefresher(isRsync ? rsyncRefresh : null);
        setActiveAuthProvider(activeProvider);
    }, [activeProvider, getAccessToken, rsyncRefresh]);

    // Register logout callback once — the interceptor calls this when refresh fails
    useEffect(() => {
        setLogoutCallback(() => {
            setDbUser(null);
            if (activeProvider === 'rsync') rsyncLogout().catch(() => {});
            else signOut(auth).catch(() => {});
        });
        return () => setLogoutCallback(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProvider]);

    // ── Firebase auth state ────────────────────────────────────────────────────
    useEffect(() => {
        if (isMock) {
            setLoading(false);
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const data = await userService.fetchDbUser(user.uid, user.email!, 'firebase');
                    setDbUser(data.success && data.exists ? (data.data as DbUser) : null);
                } catch {
                    setDbUser(null);
                }
            } else {
                setDbUser(null);
            }
            setCurrentUser(user);
            firebaseSettledRef.current = true;
            if (rsyncSettledRef.current) setLoading(false);
        });

        return unsubscribe;
    }, []);

    // ── Rsync auth state (fires once when rsync-auth-react finishes loading) ───
    useEffect(() => {
        if (isMock || rsyncLoading || rsyncSettledRef.current) return;

        void (async () => {
            // Fetch dbUser for the Rsync user only if there's no active Firebase session
            if (rsyncUser && !currentUser) {
                try {
                    const data = await userService.fetchDbUser(rsyncUser.sub, rsyncUser.email, 'rsync');
                    setDbUser(data.success && data.exists ? (data.data as DbUser) : null);
                } catch {
                    setDbUser(null);
                }
            }
            rsyncSettledRef.current = true;
            if (firebaseSettledRef.current) setLoading(false);
        })();
    }, [rsyncLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Auth actions ───────────────────────────────────────────────────────────
    const signInWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error("Error signing in with Google", error);
            throw error;
        }
    };

    const loginWithRsync = async () => {
        await rsyncLogin();
    };

    const createUser = async (userData: Omit<CreateUserDTO, 'authId' | 'email'>) => {
        let newUser: CreateUserDTO;

        if (activeProvider === 'rsync') {
            if (!rsyncUser) throw new Error('No Rsync session');
            newUser = {
                authId: rsyncUser.sub,
                email: rsyncUser.email,
                provider: 'rsync',
                ...userData,
            };
        } else {
            if (!currentUser?.email) throw new Error('No Firebase session');
            newUser = {
                authId: currentUser.uid,
                email: currentUser.email,
                provider: 'firebase',
                avatar: currentUser.photoURL || undefined,
                ...userData,
            };
        }

        const data = await userService.createUser(newUser);
        if (!data.success) throw new Error(data.message || 'Failed to create user');
        setDbUser(data.data as DbUser);
    };

    const logout = async () => {
        try {
            if (activeProvider === 'rsync') {
                await rsyncLogout();
            } else {
                await signOut(auth);
            }
            setDbUser(null);
        } catch (error) {
            console.error("Error signing out", error);
            throw error;
        }
    };

    const value: AuthContextType = {
        currentUser,
        rsyncUser,
        activeProvider,
        dbUser,
        loading,
        signInWithGoogle,
        loginWithRsync,
        createUser,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

